import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanies } from "../../redux/Dashboard/dashboardActions";
import InboxDashboard from "./InboxDashboard";
import { Grid, IconButton, Box, Typography, Button, Divider, Autocomplete, TextField, Tooltip as MuiTooltip } from "@mui/material";
import ProjectDashboard from "./ProjectDashboard";
import DocumentsDashboard from "./DocumentsDashboard";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpenseDashboard from "./ExpenseDashboard";
import SummaryStats from "./SummaryStats";
import VendorDashboard from "./VendorDashboard";
import CostCenterDashboard from "./CostCenterDashboard";
import { getRequest } from "../../globalhelper/helper";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

// Inject keyframes globally
const glowCSS = `
  @keyframes rippleEffect {
    0% { transform: scale(1); opacity: 0.8; }
    100% { transform: scale(1.8); opacity: 0; }
  }
  @keyframes smoothBounce {
    0%, 100% { transform: translateY(0) translateX(-50%); }
    50% { transform: translateY(-6px) translateX(-50%); }
  }
  @keyframes smoothBounceDown {
    0%, 100% { transform: translateY(0) translateX(-50%); }
    50% { transform: translateY(6px) translateX(-50%); }
  }
`;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Dashboard() {
  const headerRef = React.useRef(null);
  const midRowRef = React.useRef(null);
  const bottomRowRef = React.useRef(null);
  const [scrollStep, setScrollStep] = useState(0); // 0: Top, 1: Middle, 2: Bottom

  const isScrollingRef = React.useRef(false);

  useEffect(() => {
    // Only detect intersections in the top 10% of the viewport to distinguish sections clearly
    const options = { threshold: 0, rootMargin: "-5% 0% -90% 0%" };

    const observer = new IntersectionObserver((entries) => {
      // If we are currently in a manual scroll, ignore automated state detection to prevent flickering
      if (isScrollingRef.current) return;

      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === headerRef.current) setScrollStep(0);
          else if (entry.target === midRowRef.current) setScrollStep(1);
          else if (entry.target === bottomRowRef.current) setScrollStep(2);
        }
      });
    }, options);

    if (headerRef.current) observer.observe(headerRef.current);
    if (midRowRef.current) observer.observe(midRowRef.current);
    if (bottomRowRef.current) observer.observe(bottomRowRef.current);

    return () => observer.disconnect();
  }, []);

  const handleScrollToggle = () => {
    // Temporary lock to prevent observer flickering
    isScrollingRef.current = true;
    setTimeout(() => { isScrollingRef.current = false; }, 1000);

    // Cyclic manual progression for 100% reliability
    const nextStep = (scrollStep + 1) % 3;

    if (nextStep === 1) {
      midRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (nextStep === 2) {
      bottomRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Explicitly update step to bypass potential observer lag
    setScrollStep(nextStep);
  };

  const handleClearFilters = () => {
    setPeId(null);
    setFromDate(null);
    setToDate(null);
  };

  const [open, setOpen] = useState({
    inbox: false,
    project: false,
    documents: false,
    expense: false,
    vendor: false,
    costCenter: false,
  });

  const [company, setCompany] = useState(null);
  const [peId, setPeId] = useState(null);
  const [peIds, setPeIds] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const dispatch = useDispatch();
  const { companies } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (companies?.length > 0 && !company) {
      setCompany(companies[0]);
    }
  }, [companies, company]);

  useEffect(() => {
    if (!company?.orgid) return;
    let isMounted = true;
    getRequest(`api/dashboard/getIP2?orgid=${company?.orgid}`)
      .then(({ res }) => {
        if (!isMounted) return;
        const parsed = JSON.parse(res.data || "[]");
        const sorted = parsed.sort((a, b) => {
          const aVal = a?.["IP Number"] ?? "";
          const bVal = b?.["IP Number"] ?? "";
          return aVal.localeCompare(bVal);
        });
        setPeIds(sorted);
      })
      .catch((error) => console.log(error));
    return () => {
      isMounted = false;
    };
  }, [company]);

  const handleClose = () => {
    setOpen({
      inbox: false,
      project: false,
      documents: false,
      expense: false,
      vendor: false,
      costCenter: false,
    });
  };

  return (
    <Box sx={{
      width: "100%",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      bgcolor: "#f8fafc",
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(99, 102, 241, 0.03) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(16, 185, 129, 0.03) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(245, 158, 11, 0.03) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(99, 102, 241, 0.03) 0px, transparent 50%)
      `,
    }}>
      {/* ── Main Content ── */}
      <Box sx={{ flexGrow: 1, px: { xs: 1, md: 2 }, pt: 1.5, pb: 1, maxWidth: 1600, width: "100%", mx: "auto" }}>
        {/* Dashboard Header & Global Filter */}
        <Box
          ref={headerRef}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#ffffff",
            p: 1.5,
            px: 2.5,
            borderRadius: 4,
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            mb: 2,
            mt: 1,
            gap: 2
          }}>
          {/* Left Side: Global Filters */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1, flexWrap: "wrap", width: { xs: "100%", lg: "auto" } }}>
            <Typography variant="subtitle2" sx={{ color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem", mr: 1, display: { xs: 'none', sm: 'block' } }}>
              Filters
            </Typography>
            <Autocomplete
              size="small"
              id="project-filter-autocomplete"
              sx={{ flexGrow: 1, maxWidth: { sm: 240 } }}
              value={peId || null}
              options={peIds || []}
              blurOnSelect
              getOptionLabel={(option) => option?.["costcentre"] || option?.["IP Number"] || ""}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option === value || option["IP Number"] === value["IP Number"];
              }}
              onChange={(event, newValue) => setPeId(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Projects"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2.5,
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "#e2e8f0" },
                      "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1px", bgcolor: "#fff" },
                    }
                  }}
                />
              )}
            />
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="From Date"
                value={fromDate}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => setFromDate(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{
                      width: { xs: "100%", sm: 160 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5,
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "#e2e8f0" },
                        "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1px", bgcolor: "#fff" },
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="To Date"
                value={toDate}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => setToDate(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{
                      width: { xs: "100%", sm: 160 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5,
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "#e2e8f0" },
                        "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1px", bgcolor: "#fff" },
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            <MuiTooltip title="Clear Filters">
              <IconButton
                onClick={handleClearFilters}
                sx={{
                  bgcolor: "#f1f5f9",
                  color: "#475569",
                  "&:hover": { bgcolor: "#e2e8f0", color: "#1e293b" },
                  borderRadius: 2.5,
                  ml: 1
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </MuiTooltip>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0", borderRightWidth: 2, my: 0.5, display: { xs: 'none', lg: 'block' } }} />

          {/* Right Side: Company Select */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: { xs: "100%", lg: 380 }, pl: { lg: 1 } }}>
            <Typography variant="subtitle2" sx={{ color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem", display: { xs: 'none', sm: 'block' } }}>
              Company
            </Typography>
            <Autocomplete
              fullWidth
              id="company-selector-autocomplete"
              disableClearable
              blurOnSelect
              options={companies || []}
              value={company || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  setCompany(newValue);
                  // Optional: clear project filter when switching company to prevent mismatched requests
                  setPeId(null);
                }
              }}
              getOptionLabel={(option) => option?.orgname || ""}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.orgid === value.orgid;
              }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Company"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2.5,
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "#e2e8f0" },
                      "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1px", bgcolor: "#fff" },
                    }
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Summary Stats Row */}
        <SummaryStats company={company} peid={peId?.["IP Number"] || ""} fromDate={fromDate} toDate={toDate} />

        {/* Inject Styles */}
        <style>{glowCSS}</style>

        {/* ── Row 1: Inbox | Expense | Vendor ── */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={6}>
            <InboxDashboard open={open.inbox} setOpen={setOpen} company={company} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ExpenseDashboard
              open={open.expense}
              setOpen={setOpen}
              company={company}
              globalPeId={peId}
              setGlobalPeId={setPeId}
              globalPeIds={peIds}
              globalFromDate={fromDate}
              globalToDate={toDate}
            />
          </Grid>
        </Grid>

        {/* Fixed Scroll Toggle Button */}
        <Box sx={{
          position: "fixed",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2000,
          display: Object.values(open).some(Boolean) ? "none" : "flex",
          justifyContent: "center",
          animation: scrollStep === 2
            ? "smoothBounce 3s infinite ease-in-out"
            : "smoothBounceDown 3s infinite ease-in-out",
        }}>
          {/* Glow/Pulse Wrapper */}
          <Box sx={{
            position: "absolute",
            width: 50,
            height: 50,
            borderRadius: "50%",
            border: "2px solid #6366f1",
            animation: "rippleEffect 2s infinite ease-out",
            pointerEvents: "none",
            top: "50%",
            left: "50%",
            mt: -3.1,
            ml: -3.1
          }} />

          <IconButton
            onClick={handleScrollToggle}
            sx={{
              bgcolor: "#ffffff",
              color: "#6366f1",
              p: 1.5,
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              border: "1px solid rgba(99, 102, 241, 0.1)",
              "&:hover": {
                bgcolor: "#ffffff",
                transform: "scale(1.1)",
                boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
              },
              transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
            }}
          >
            {scrollStep === 2 ? <KeyboardArrowUpIcon sx={{ fontSize: 32 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 32 }} />}
          </IconButton>
        </Box>

        {/* ── Row 2: Vendor | CostCenter | Project | Documents ── */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={6} ref={midRowRef}>
            <VendorDashboard
              open={open.vendor}
              setOpen={setOpen}
              company={company}
              globalFromDate={fromDate}
              globalToDate={toDate}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CostCenterDashboard open={open.costCenter} setOpen={setOpen} company={company} />
          </Grid>
          <Grid item xs={12} md={6} ref={bottomRowRef}>
            <ProjectDashboard
              open={open.project}
              setOpen={setOpen}
              company={company}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DocumentsDashboard
              open={open.documents}
              setOpen={setOpen}
              company={company}
              globalPeId={peId}
              setGlobalPeId={setPeId}
              globalPeIds={peIds}
              globalFromDate={fromDate}
              globalToDate={toDate}
            />
          </Grid>
        </Grid>

        {/* Bottom Buffer for 100% reliable 3rd section visibility */}
        <Box sx={{ height: "40vh" }} />
      </Box>

      {/* ── Expand Dialogs ── */}
      <Dialog
        open={open.inbox || open.project || open.documents || open.expense || open.vendor || open.costCenter}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "flex-end" }}>
          <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {open.inbox && <InboxDashboard open={true} setOpen={setOpen} company={company} /> ||
            open.project && <ProjectDashboard open={true} setOpen={setOpen} company={company} globalPeId={peId} setGlobalPeId={setPeId} globalPeIds={peIds} /> ||
            open.documents && <DocumentsDashboard open={true} setOpen={setOpen} company={company} globalPeId={peId} setGlobalPeId={setPeId} globalPeIds={peIds} /> ||
            open.expense && <ExpenseDashboard open={true} setOpen={setOpen} company={company} globalPeId={peId} setGlobalPeId={setPeId} globalPeIds={peIds} /> ||
            open.vendor && <VendorDashboard open={true} setOpen={setOpen} company={company} /> ||
            open.costCenter && <CostCenterDashboard open={true} setOpen={setOpen} company={company} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Dashboard;
